import { fmtNumber } from './format';
export type TranslationMap = Record<string, string>;
export type Replacements = Record<string, string | number | boolean | null | undefined>;

const PLURAL_CATEGORIES = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;
const PLURAL_RULES = new Map<string, Intl.PluralRules>();

function hasPluralVariants(map: TranslationMap, key: string): boolean {
  return PLURAL_CATEGORIES.some((category) => map[`${key}.${category}`] !== undefined);
}

function pluralCategory(locale: string, count: number): Intl.LDMLPluralRule {
  try {
    let rules = PLURAL_RULES.get(locale);
    if (!rules) {
      rules = new Intl.PluralRules(locale);
      PLURAL_RULES.set(locale, rules);
    }
    return rules.select(count);
  } catch {
    // Unbekannte Sprach-Tags duerfen die bisherige EN-Rueckfallkette nicht
    // unterbrechen.
    let rules = PLURAL_RULES.get('en');
    if (!rules) {
      rules = new Intl.PluralRules('en');
      PLURAL_RULES.set('en', rules);
    }
    return rules.select(count);
  }
}

// Ganzzahlige Platzhalter werden nur gruppiert, wenn sie Mengen/Zaehler sind
// (nie Jahre, Kennungen, Bestellnummern). Zaehlerpaare wie `{n}/{max}` muessen
// zusammen formatiert werden, sonst steht „1.234/2000“ im Zeichenzaehler.
const GROUPED_NAME = /(coin|balance|amount|cost|price|minute|remaining|discount|weekly|monthly|spent|earned|max|cap|limit|used|current|characters|chars|seconds|replies)/i;
const EXACT_COUNTER_NAME = /^(left|total|count|turns|n)$/;

function replacePlaceholders(value: string, replacements: Replacements | undefined, locale: string): string {
  if (!replacements) return value;
  return value.replace(/\{([a-zA-Z0-9_.-]+)\}/g, (match, name: string) => {
    const replacement = replacements[name];
    if (replacement === undefined || replacement === null) return match;
    if (typeof replacement === 'number') {
      if (!Number.isFinite(replacement)) return '—';
      // Values remain numeric for plural selection and billing. Only the label is
      // rounded; identifiers and years are never grouped.
      if (!Number.isInteger(replacement) || GROUPED_NAME.test(name) || EXACT_COUNTER_NAME.test(name)) {
        return fmtNumber(replacement, locale);
      }
    }
    return String(replacement);
  });
}

export function translateValue(
  key: string,
  activeMap: TranslationMap,
  enMap: TranslationMap,
  replacements?: Replacements,
  locale = 'en',
): string {
  const count = replacements?.count;
  if (typeof count === 'number' && Number.isFinite(count)) {
    if (hasPluralVariants(activeMap, key)) {
      const activeCategory = pluralCategory(locale, count);
      const englishCategory = pluralCategory('en', count);
      const raw = activeMap[`${key}.${activeCategory}`]
        ?? activeMap[`${key}.other`]
        ?? enMap[`${key}.${englishCategory}`]
        ?? enMap[`${key}.other`]
        ?? activeMap[key]
        ?? enMap[key]
        ?? key;
      return replacePlaceholders(raw, replacements, locale);
    }

    // Eine vorhandene aktive Basis-Uebersetzung behaelt Vorrang. Nur wenn sie
    // fehlt, greift wie bisher EN — dort dann mit dessen eigenen Pluralregeln.
    if (activeMap[key] === undefined && hasPluralVariants(enMap, key)) {
      const englishCategory = pluralCategory('en', count);
      const raw = enMap[`${key}.${englishCategory}`]
        ?? enMap[`${key}.other`]
        ?? enMap[key]
        ?? key;
      return replacePlaceholders(raw, replacements, locale);
    }
  }

  const raw = activeMap[key] ?? enMap[key] ?? key;
  return replacePlaceholders(raw, replacements, locale);
}
